import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import FomoFiLogo from "@/components/brand/FomoFiLogo";
import RedesignNavItem from "@/components/layout/RedesignNavItem";
import { TooltipProvider } from "@/components/ui/tooltip";
import { REDESIGN_NAV_ITEMS, navItemIsActive } from "@/config/redesignNavItems";
import { useLocation } from "react-router-dom";
import { SidebarAccountMenu } from "@/components/layout/AccountMenu";
import { useProfile } from "@/hooks/use-profile";

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

// Five items and the account menu at the foot (Anthony's redesign, 2026-10-13). The
// account menu came back down here from the upper-right ProfileMenu (feedback #5/#6
// had moved it up); below lg the header avatar opens the same menu. The "Zero net
// sum" promo tile stays removed (feedback #7).
export default function AppSidebarRedesign({ collapsed, onToggle }: AppSidebarProps) {
  const { pathname } = useLocation();
  // Q6 (Paul): this pay period's purchases to sort, from the one server function
  // (purchases_to_sort_count, #1985). Home's count and checklist step 2 read the same.
  const toSortCount = useProfile().data?.features?.activity_to_sort_count ?? 0;
  return (
    <TooltipProvider>
      <aside
        className={cn(
          "hidden lg:flex flex-col fixed inset-y-0 left-0 z-40 bg-sidebar text-sidebar-foreground rounded-r-2xl transition-all duration-300",
          collapsed ? "w-[72px]" : "w-64",
        )}
      >
        {/* Logo + collapse toggle */}
        <div
          className={cn(
            "flex h-16 items-center shrink-0 border-b border-sidebar-border",
            collapsed ? "justify-center px-0" : "justify-between px-5",
          )}
        >
          <FomoFiLogo size="md" showText={!collapsed} />
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-sidebar-foreground/40 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* Nav items */}
        <nav
          aria-label="Main navigation"
          className={cn("flex-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")}
        >
          <ul role="list" className="flex flex-col gap-1.5">
            {REDESIGN_NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <RedesignNavItem
                  to={item.to}
                  label={item.label}
                  icon={item.icon}
                  collapsed={collapsed}
                  active={navItemIsActive(item, pathname)}
                  badge={item.to === "/transactions" ? toSortCount : undefined}
                />
              </li>
            ))}
          </ul>
        </nav>

        <div className={cn("shrink-0 pb-3", collapsed ? "px-2" : "px-3")}>
          <SidebarAccountMenu collapsed={collapsed} />
        </div>

        {/* FP-181: the feedback stack and Back to top dock here from lg (index.css), so
            the menu above ends where they begin, at every window height. */}
        <div aria-hidden="true" className="h-[var(--sidebar-fab-reserve)] shrink-0" />
      </aside>
    </TooltipProvider>
  );
}
